/**
 * User-level travel vibes — defaults that pre-fill the publish form and show
 * up as small badges on the public profile.
 *
 * Three-state booleans (yes / no / not set) are rendered as a 3-segment toggle.
 * "Not set" is the default for existing users; it means "no preference, falls
 * back to per-ride defaults in the publish flow" and "hide from public profile".
 */
import type { Vibes } from '@waylo/shared';
import { useT } from '../lib/i18n';
import { hapticSelection } from '../lib/telegram';
import { Card, CardHint, CardTitle } from './Card';
import { cn } from '../lib/cn';

interface Props {
  value: Vibes;
  onChange: (next: Vibes) => void;
}

type TriState = boolean | null;

export function VibesCard({ value, onChange }: Props): JSX.Element {
  const { t } = useT();

  const setField = <K extends keyof Vibes>(key: K, v: Vibes[K]): void => {
    hapticSelection();
    onChange({ ...value, [key]: v });
  };

  return (
    <Card className="space-y-3">
      <CardTitle>{t('vibes.title')}</CardTitle>
      <CardHint>{t('vibes.hint')}</CardHint>

      <TriToggle
        label={t('vibes.music')}
        value={value.music}
        onChange={(v) => setField('music', v)}
      />
      <TriToggle
        label={t('vibes.smoking')}
        value={value.smoking}
        onChange={(v) => setField('smoking', v)}
      />
      <TriToggle
        label={t('vibes.pets')}
        value={value.pets}
        onChange={(v) => setField('pets', v)}
      />
      <TriToggle
        label={t('vibes.womenOnly')}
        value={value.womenOnly}
        onChange={(v) => setField('womenOnly', v)}
      />

      <div className="space-y-1.5">
        <p className="text-sm font-medium text-tg-text">{t('vibes.chatLevel')}</p>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { v: null, k: 'vibes.notSet' as const },
            { v: 0 as const, k: 'vibes.chat.quiet' as const },
            { v: 1 as const, k: 'vibes.chat.sometimes' as const },
            { v: 2 as const, k: 'vibes.chat.chatty' as const },
          ].map(({ v, k }) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => setField('chatLevel', v as Vibes['chatLevel'])}
              className={cn(
                'rounded-input border px-2 py-2 text-xs font-medium transition',
                value.chatLevel === v
                  ? 'border-transparent bg-[var(--border-medium)] font-semibold text-tg-text'
                  : 'border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-tg-hint',
              )}
            >
              {t(k)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-sm font-medium text-tg-text">{t('vibes.luggage')}</p>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { v: null, k: 'vibes.notSet' as const },
            { v: 'small' as const, k: 'luggage.small' as const },
            { v: 'medium' as const, k: 'luggage.medium' as const },
            { v: 'large' as const, k: 'luggage.large' as const },
          ].map(({ v, k }) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => setField('luggage', v)}
              className={cn(
                'rounded-input border px-2 py-2 text-xs font-medium transition',
                value.luggage === v
                  ? 'border-transparent bg-[var(--border-medium)] font-semibold text-tg-text'
                  : 'border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-tg-hint',
              )}
            >
              {t(k)}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}

function TriToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: TriState;
  onChange: (v: TriState) => void;
}): JSX.Element {
  const { t } = useT();
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-tg-text">{label}</p>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { v: null as TriState, k: 'vibes.notSet' as const },
          { v: true, k: 'vibes.yes' as const },
          { v: false, k: 'vibes.no' as const },
        ].map(({ v, k }) => (
          <button
            key={String(v)}
            type="button"
            onClick={() => onChange(v)}
            className={cn(
              'rounded-input border px-2 py-2 text-xs font-medium transition',
              value === v
                ? 'border-transparent bg-[var(--border-medium)] font-semibold text-tg-text'
                : 'border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-tg-hint',
            )}
          >
            {t(k)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Compact read-only display of vibes — used on the public profile.
 * Shows a small badge for each non-null vibe. Returns null if nothing set.
 */
export function VibesBadges({ vibes }: { vibes: Vibes }): JSX.Element | null {
  const { t } = useT();
  const items: Array<{ key: string; label: string }> = [];

  if (vibes.music !== null)
    items.push({
      key: 'music',
      label: vibes.music ? t('ride.pref.music.yes') : t('ride.pref.music.no'),
    });
  if (vibes.smoking !== null)
    items.push({
      key: 'smoking',
      label: vibes.smoking ? t('ride.pref.smoking.yes') : t('ride.pref.smoking.no'),
    });
  if (vibes.pets !== null)
    items.push({
      key: 'pets',
      label: vibes.pets ? t('ride.pref.pets.yes') : t('ride.pref.pets.no'),
    });
  if (vibes.womenOnly === true) items.push({ key: 'women', label: t('ride.pref.womenOnly') });
  if (vibes.chatLevel !== null) {
    const key =
      vibes.chatLevel === 0 ? 'vibes.chat.quiet' : vibes.chatLevel === 1 ? 'vibes.chat.sometimes' : 'vibes.chat.chatty';
    items.push({ key: 'chat', label: t(key as 'vibes.chat.quiet') });
  }
  if (vibes.luggage)
    items.push({ key: 'luggage', label: `${t('ride.pref.luggage')}: ${t(`luggage.${vibes.luggage}` as 'luggage.medium')}` });

  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span
          key={it.key}
          className="rounded-pill bg-tg-bg-secondary px-2.5 py-1 text-xs text-tg-text"
        >
          {it.label}
        </span>
      ))}
    </div>
  );
}
